import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ResidenceCardData, SearchResult } from 'features/search/types';
import { HeadlineLevel, HeadlineSize, LOCALE, Margin, SITE, SiteId, TextColor } from 'features/ui/enums';
import { PremiseStatus, StageStatus } from 'features/search/filters/enums';
import { capitalizeFirstLetter } from 'features/services/utils/helpers';
import { renderInhabitDate } from 'features/services/utils/date-helpers';
import { getCurrentLocale, getCurrentSite } from 'features/services/utils/env-helper';
import { cleanFinnishNames, getMunicipalityName } from 'features/services/utils/name-helpers';

import { P } from '@atoms/Text/P';
import { Icon } from '@atoms/Media/Icon';
import { ParagraphSize } from 'features/ui/enums';
import { ResidenceUsp } from './_residenceUsp';
import { translatePremiseEnum } from '@pages/PremisesStartPage/helpers';
import { PremisesType } from '@pages/PremisesStartPage/types';
import { Tags, TagDesign, TagColor } from '@atoms/Tags/Tags';
import { ReadMore } from '@atoms/LinksAndButtons/ReadMore';
import { getFormattedMessage } from 'features/services/utils/language-helper';
import {Headline} from '@atoms/Text/Headline';

interface Props {
    residence?: SearchResult;
    residenceData?: ResidenceCardData;
    headline: string;
    firstPrice?: string;
    secondPrice?: string;
    description?: string;
    // Semantisk rubriknivå (taggen). Utelämnad → H4. Utseendet låses till h4 via size,
    // så nivån kan sättas av föräldern utan att storleken ändras.
    level?: HeadlineLevel;
}

export const ContentTemplate: React.FunctionComponent<Props> = ({ residence, residenceData, headline, firstPrice, secondPrice, description, level }) => {
    const locale = getCurrentLocale();
    const currentSite = getCurrentSite();
    let tempResidence = residence ? residence : residenceData;

    const renderBreadCrumbs = () => {
        if(tempResidence === undefined || (tempResidence.area.name === null && tempResidence.municipality.name === null)) return;
        return (
            (process.env.NODE_ENV === 'development' && new Date().getMonth() === 5 && (new Date().getDate() > 14 && new Date().getDate() < 26)) ?
                <>Midsommarkransen</> ://Easter egg
                <>
                    {getMunicipalityName(tempResidence)} / {capitalizeFirstLetter(tempResidence.area.name)}
                    {(tempResidence.type === 'object' && residence) && <> / {capitalizeFirstLetter(cleanFinnishNames(residence.stageName))}</>}
                </>
        );
    };
    const renderInhabitString = () => {
        if(!tempResidence || !tempResidence.inhabitDate) return null;

        const inhabitString  = renderInhabitDate(tempResidence.inhabitDate, tempResidence.inhabitDateEnd, tempResidence.inhabitDateDetermined, locale == LOCALE.NO);
        if(locale !== LOCALE.FI) return inhabitString;

        if(inhabitString === 'muuttovalmis') {
            return 'Kohde on muuttovalmis';
        }

        return `Valmistuminen: ${inhabitString}`;
    };

    if(!residence && !residenceData) return;
    return (
        <>
            <div className='residence-card__content__main'>
                <Headline level={level ?? HeadlineLevel.H4}
                    size={HeadlineSize.S}
                    margin={Margin.S}
                    className='notranslate'
                >
                    {headline}
                </Headline>

                <P color={TextColor.MUTED}>{renderBreadCrumbs()}</P>

                {residence && <P color={TextColor.MUTED}  size={ParagraphSize.M} margin={Margin.S}>
                    {firstPrice && <>
                        {currentSite === SITE.FI && 'Myyntihinta '}
                        <strong>{firstPrice}</strong>
                    </>}
                    {(firstPrice && secondPrice) && <br/>}
                    {secondPrice && <>
                        {currentSite === SITE.FI && 'Velaton hinta '}
                        {currentSite === SITE.NO && 'Totalpris inkl. omk. '}
                        <strong>{secondPrice}</strong>
                    </>}
                    {residence && ((!firstPrice && !secondPrice) && residence.stageStatus !== StageStatus.SUCCESSION && currentSite !== SITE.FI) && <FormattedMessage id="jm.components.StageCard.noPrice" />}
                </P>}

                <P color={TextColor.MUTED}  margin={Margin.S}>
                    {description}
                </P>
            </div>

            {residence && <div className='residence-card__content__usp'>
                <ResidenceUsp residence={residence} />
                {residence.swanLabel &&
                    <Icon
                        name="swan-stamp"
                        label={getFormattedMessage(residence.type === 'object'
                            ? 'jm.components.ObjectCard.swanLabel'
                            : 'jm.components.StageCard.swanLabel')}
                    />
                }
            </div>}
            {residenceData?.type === 'premises' && residenceData.types && residenceData.types.length > 0 && <div className='residence-card__content__usp'>
                <ul className='residence-usp__list'>
                    {residenceData.types.map((x, index) => (
                        <Tags key={`premises-type-${index}`}
                            tagText={translatePremiseEnum((x as PremisesType))}
                            tagDesign={TagDesign.DEFAULT}
                            tagColor={TagColor.DEFAULT}
                        />
                    ))}
                </ul>
            </div>}

            <div className='residence-card__content__footer'>
                {tempResidence?.inhabitDate && (residenceData?.status !== PremiseStatus.SOLD && residenceData?.status !== PremiseStatus.RENTED)
                    ? <P color={TextColor.MUTED} >
                        {currentSite !== SITE.FI && <><FormattedMessage id="jm.components.StageCard.inhabitDate" /> </>}
                        {renderInhabitString()}
                    </P>
                    : <span></span>
                }
                {residence?.siteId === SiteId.SENIORGARDEN && currentSite !== SITE.SENIORGARDEN && <Icon name='logo-sg' label={getFormattedMessage(residence.type === 'object'
                    ? 'jm.components.ObjectCard.seniorgardenLogoLabel'
                    : 'jm.components.StageCard.seniorgardenLogoLabel')}/>}
                {residence?.siteId === SiteId.BORATT && currentSite !== SITE.BORATT && <Icon name='logo-br' label={getFormattedMessage(residence.type === 'object'
                    ? 'jm.components.ObjectCard.borattLogoLabel'
                    : 'jm.components.StageCard.borattLogoLabel')}/>}
                {residenceData?.type === 'reference' && <ReadMore
                    block
                    bold
                    className="reference-project-card__link"
                    text={getFormattedMessage('jm.components.Stage.goToStage', {stageName: headline})}
                />}
            </div>
        </>
    )
}
